import angular from "angular";

import {
  Ajax,
  Dates,
  InputEnumOption,
  Invoice,
  InvoiceListItem,
  Invoices,
  InvoiceStatus,
  InvoiceType,
  Numeric,
  PaymentChannel,
  SystemRole,
  Translations,
  BicHelper,
  Configs,
} from "@salaxy/core";
import { Barcodes, QrCodes } from "@salaxy/reports";
import { InvoicesService, SessionService, UiHelpers } from "../../services";
import { InvoiceCrudController } from "./InvoiceCrudController";
/**
 * Extends the InvoiceCrudController to provide
 * company invoices handling functionality.
 */
export class CompanyInvoicesController extends InvoiceCrudController {
  /**
   * For NG-dependency injection
   */
  public static $inject = [
    "Invoices",
    "UiHelpers",
    "$location",
    "$routeParams",
    "InvoicesService",
    "AjaxNg1",
    "SessionService",
    "$http",
    "$timeout",
    "$sce",
  ];

  /** Default channel for payments */
  public channel: PaymentChannel;

  /** Month for invoices query */
  private queryMonth = Dates.format(Dates.getToday(), "YYYY-MM-01");

  /** Queries for lists: Groups statuses to logical groups. */
  public queryOptions: InputEnumOption[] = [
    {
      value: "unpaid",
      text: Translations.get(
        "SALAXY.NG1.InvoicesComponent.statusWaitingConfirmation"
      ),
      ui: () => ({
        $filter: `data/channel eq '${this.channel}' and (entityType eq '${InvoiceType.Net}' or entityType eq '${InvoiceType.Foreclosure}' or entityType eq '${InvoiceType.Tax}' or  entityType eq '${InvoiceType.Union}' or  entityType eq '${InvoiceType.Payroll}') and ( status eq '${InvoiceStatus.Preview}' or status eq '${InvoiceStatus.Unread}' or status eq '${InvoiceStatus.Read}' or status eq '${InvoiceStatus.WaitingConfirmation}')`,
        $top: undefined,
        $orderby: "endAt asc",
      }),
    },
    {
      value: "paid",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusPaid"),
      ui: () => ({
        $filter: `${
          this.dateFilterQueryOption.ui().dateFilter
        }data/channel eq '${
          this.channel
        }' and (entityType eq '${InvoiceType.Net}' or entityType eq '${
          InvoiceType.Foreclosure
        }' or entityType eq '${InvoiceType.Tax}' or  entityType eq '${
          InvoiceType.Union
        }' or  entityType eq '${InvoiceType.Payroll}') and ( status eq '${
          InvoiceStatus.Paid
        }' or status eq '${InvoiceStatus.PaymentStarted}' or status eq '${
          InvoiceStatus.Canceled
        }')`,
        $top: undefined,
        $orderby: "endAt asc",
      }),
    },
    {
      value: "all",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusAll"),
      ui: () => ({
        $filter: `${
          this.dateFilterQueryOption.ui().dateFilter
        }data/channel eq '${
          this.channel
        }' and (entityType eq '${InvoiceType.Net}' or entityType eq '${
          InvoiceType.Foreclosure
        }' or entityType eq '${InvoiceType.Tax}' or  entityType eq '${
          InvoiceType.Union
        }' or  entityType eq '${InvoiceType.Payroll}') `,
        $top: undefined,
        $orderby: "endAt asc",
      }),
    },
  ];

  public queryDateFilterOptions: InputEnumOption[] = [
    {
      value: "default",
      text: `${Dates.getMonth(Dates.add(this.queryMonth, -1, "month"))}-${Dates.getMonth(Dates.add(this.queryMonth, 1, "month"))}/${Dates.getYear(this.queryMonth)}`,
      ui: () => ({
        dateFilter: `endAt ge ${Dates.add(
          this.queryMonth,
          -1,
          "month"
        )} and endAt le ${Dates.startEnd(
          Dates.add(this.queryMonth, 1, "month"),
          "end-month"
        )} and `,
      }),
    },
    {
      value: "fromStartOfYear",
      text: `${Translations.get("SALAXY.UI_Terms.year")} ${Dates.getYear(this.queryMonth)}`,
      ui: () => ({
        dateFilter: `endAt ge ${Dates.startEnd(
          this.queryMonth,
          "start-year"
        )} and endAt le ${Dates.startEnd(
          this.queryMonth,
          "end-year"
        )} and `,
      }),
    },
    {
      value: "all",
      text: Translations.get("SALAXY.NG1.InvoicesCompany.queryFilterDateAll"),
      ui: () => ({
        dateFilter: ``,
      }),
    },
    {
      value: "other",
      text: Translations.get("SALAXY.NG1.InvoicesCompany.queryFilterDateOther"),
      ui: () => ({
        dateFilter: ``,
      }),
    },
  ];

  /** The current query */
  public queryOption: InputEnumOption = this.queryOptions[0];
  public dateFilterQueryOption: InputEnumOption =
    this.queryDateFilterOptions[0];

  private qrCodesCache: { [key: string]: string } = {};

  constructor(
    invoicesApi: Invoices,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    invoiceService: InvoicesService,
    private companyAjax: Ajax,
    private session: SessionService,
    private $http: angular.IHttpService,
    private $timeout: angular.ITimeoutService,
    private $sce: angular.ISCEService
  ) {
    // Dependency injection
    super(
      invoicesApi,
      uiHelpers,
      $location,
      $routeParams,
      invoiceService,
      companyAjax
    );
  }

  /** Intitializes the controller */
  public $onInit() {
    this.channel ||= PaymentChannel.PalkkausInvoices;
    super.$onInit();
  }

  /** For list components, gets the OData query options. */
  public getListOptions() {
    return this.uiHelpers.cache(this, "listOptions", () => {
      return this.queryOption.ui();
    });
  }

  /**
   * Sets the date filter query option.
   * @param option - The input enum option.
   */
  public setDateFilterQueryOption(option: InputEnumOption) {
    const oldDateFilterQueryOption = this.dateFilterQueryOption;
    this.dateFilterQueryOption = option;
    if (this.dateFilterQueryOption.value !== "other") {
      this.doQuery();
    } else {
      const logic = {
        startDate: Dates.add(this.queryMonth, -1, "month"),
        endDate: Dates.add(this.queryMonth, 1, "month"),
      };
      this.uiHelpers
        .openEditDialog(
          "salaxy-components/modals/invoices/company-invoices-date-filter-dialog.html",
          null,
          logic
        )
        .then((result) => {
          if (result.action === "ok") {
            this.dateFilterQueryOption.ui = () => ({
              dateFilter: `endAt ge ${result.logic.startDate} and endAt le ${result.logic.endDate} and `,
            });
            this.dateFilterQueryOption.text = `Muu ${result.logic.startDate} - ${result.logic.endDate}`;
            this.doQuery();
          } else {
            this.dateFilterQueryOption = oldDateFilterQueryOption;
          }
        });
    }
  }

  /** Refreshes query */
  public doQuery() {
    this.odataController.setQueryOptions(this.queryOption.ui());
    this.odataController.refresh();
  }

  /** Dropdown options for marking invoice as paid */
  public getMarkAsPaidOptions(items: InvoiceListItem[]): InputEnumOption[] {
    return this.uiHelpers.cache(
      this,
      "markAsPaidOptions",
      () => {
        const result: InputEnumOption[] = [];
        const filterInvoices = (type: InvoiceType) =>
          items.filter(
            (item) =>
              item.entityType === type &&
              item.status !== InvoiceStatus.Paid &&
              item.status !== InvoiceStatus.Canceled
          );

        const netInvoices = filterInvoices(InvoiceType.Net);
        const taxInvoices = filterInvoices(InvoiceType.Tax);
        const unionInvoices = filterInvoices(InvoiceType.Union);
        const foreclosureInvoices = filterInvoices(InvoiceType.Foreclosure);
        result.push({
          value: "all",
          text: Translations.get("SALAXY.NG1.InvoicesCompany.markAsPaidOptions.all"),
        });
        if (netInvoices.length > 0) {
          result.push({
            value: InvoiceType.Net,
            text: Translations.get("SALAXY.NG1.InvoicesCompany.markAsPaidOptions.netInvoices"),
          });
        }

        if (taxInvoices.length > 0) {
          result.push({
            value: InvoiceType.Tax,
            text: Translations.get("SALAXY.NG1.InvoicesCompany.markAsPaidOptions.taxInvoices"),
          });
        }

        if (unionInvoices.length > 0) {
          result.push({
            value: InvoiceType.Union,
            text: Translations.get("SALAXY.NG1.InvoicesCompany.markAsPaidOptions.unionInvoices"),
          });
        }

        if (foreclosureInvoices.length > 0) {
          result.push({
            value: InvoiceType.Foreclosure,
            text: Translations.get("SALAXY.NG1.InvoicesCompany.markAsPaidOptions.foreclosureInvoices"),
          });
        }
        result.push({
          value: "removeAll",
          text: Translations.get("SALAXY.NG1.InvoicesCompany.markAsPaidOptions.removeAll"),
        });
        return result;
      },
      () => items.map((item) => item.id)
    );
  }

  /**
   * Selects invoices of a specific type and marks them as selected.
   * @param action - The type of invoices to select.
   */
  public selectInvoicesByType(action: InvoiceType | "all" | "removeAll") {
    if(action === "removeAll") {
      this.odataController.unselectAll();
      return;
    }

    if(action === "all") {
      this.odataController.selectAll();
      return;
    }

    const invoices = this.odataController.items.filter(
      (x) =>
        x.entityType === action &&
        x.status !== InvoiceStatus.Paid &&
        x.status !== InvoiceStatus.Canceled
    );
    for (const invoice of invoices) {
      this.odataController.setSelected(invoice, true);
    }
  }

  /**
   * Determines whether to show the "Mark as Paid" button for a given invoice.
   * @param invoice - The invoice to check.
   * @returns A boolean indicating whether to show the button.
   */
  public showMarkAsPaidButton(invoice: InvoiceListItem): boolean {
    return (
      invoice.status === InvoiceStatus.Preview ||
      invoice.status === InvoiceStatus.Unread
    );
  }

  /**
   * Determines whether to show the "Mark as Canceled" button for a given invoice.
   * @param invoice - The invoice to check.
   * @returns A boolean indicating whether to show the button.
   */
  public showMarkAsCanceledButton(invoice: InvoiceListItem): boolean {
    return (
      invoice.status === InvoiceStatus.Paid ||
      invoice.status === InvoiceStatus.Unread ||
      invoice.status === InvoiceStatus.Preview
    );
  }

  /**
   * Determines whether to show the IR cancel information text for a given invoice.
   * @param invoice - The invoice item to check.
   * @returns `true` if the invoice entity type is `Net` and the status is `Paid`, otherwise `false`.
   */
  public showIrCancelInfoText(invoice: InvoiceListItem): boolean {
    return invoice.entityType === InvoiceType.Net && invoice.status === InvoiceStatus.Paid;
  }

  /**
   * Shows a dialog for a single invoice.
   * @param item Invoice item.
   */
  public showInvoiceDialog(item: InvoiceListItem) {
    this.uiHelpers
      .openEditDialog(
        "salaxy-components/modals/invoices/company-invoice-dialog.html",
        item,
        { parent: this },
        "lg"
      )
      .then((response: any) => {
        if (response.action === "ok") {
          this.getInvoice(item, null, null).then((result) => {
            (item.data as any).statusAt = (result.header as any).statusAt;
            item.status = result.header.status;
            this.refreshPeriodicInvoices();
          });
        }
      });
  }

  /**
   * Marks selected invoices as paid.
   * This method filters the selected invoices to exclude those that are already paid or canceled.
   * It then shows a loading indicator and processes each invoice by marking it as paid.
   * After processing all invoices, it refreshes the periodic invoices and dismisses the loading indicator.
   * @returns A promise that resolves when the operation is complete.
   */
  public async markInvoicesAsPaid() {
    const invoices = this.odataController.selectedItems.filter(
      (item) =>
        item.status != InvoiceStatus.Paid &&
        item.status != InvoiceStatus.Canceled
    );
    const loader = this.uiHelpers.showLoading(
      "SALAXY.NG1.InvoicesCompany.markingAsPaid"
    );
    for (const [index, invoice] of invoices.entries()) {
      const reader = this.getInvoiceReader(invoice, InvoiceStatus.Paid, false);
      loader.setText(`${index + 1}/${invoices.length}`);
      await reader.read([], null);
    }
    await this.refreshPeriodicInvoices();
    loader.dismiss();
  }

  /** Update status of single invoice. */
  public updateInvoiceStatus(item: InvoiceListItem, status: InvoiceStatus) {
    const loading = this.uiHelpers.showLoading(
      status === InvoiceStatus.Paid
        ? "SALAXY.NG1.InvoicesCompany.markingAsPaid"
        : "SALAXY.NG1.InvoicesCompany.markingAsCanceled"
    );
    const reader = this.getInvoiceReader(item, status);
    reader.read([], null).then(() => {
      loading.dismiss();
    });
  }

  /** Data reader for one item. */
  public getInvoiceReader = (
    item: InvoiceListItem,
    status: InvoiceStatus,
    updatePeriodicInvoices = true
  ) => {
    return {
      /** Function to read data into given array */
      read: (arr: Invoice[], message: string): Promise<void> => {
        const showConfirmation = (): Promise<boolean> => {
          const warning = this.getEprWarning([item], status);
          if (warning) {
            return this.uiHelpers.showConfirm(
              Translations.get("SALAXY.NG1.InvoicesCompany.invoiceReader.eprWarningHeading"),
              warning
            );
          } else {
            if(status === InvoiceStatus.Canceled) {
              const getConfirmText = (item: InvoiceListItem): string => {
                if(item.entityType === "net" && item.status === InvoiceStatus.Unread) {
                  return Translations.get("SALAXY.NG1.InvoicesCompany.invoiceReader.cancellationTexts.invoiceCancelingNetInvoiceText");
                }
                if(item.entityType === "tax" && item.status !== InvoiceStatus.Paid) {
                  return Translations.get("SALAXY.NG1.InvoicesCompany.invoiceReader.cancellationTexts.invoiceCancelingTax");
                }
                return Translations.get("SALAXY.NG1.InvoicesCompany.invoiceReader.cancellationTexts.invoiceCancelingInvoiceText");
              };

              return this.uiHelpers.showConfirm(
                Translations.get("SALAXY.NG1.InvoicesCompany.invoiceReader.cancellationTexts.invoiceCancelingHeading"),
                getConfirmText(item),
                Translations.get("SALAXY.NG1.InvoicesCompany.companyInvoiceDialog.buttons.save"),
                Translations.get("SALAXY.NG1.InvoicesCompany.companyInvoiceDialog.buttons.cancel")
              );
            }
          }
          return Promise.resolve(true);
        };
        return showConfirmation().then((result) => {
          if (result) {
            (item as any)["isReading" + (status || "")] = true;
            return this.getInvoice(item, message, status).then((data) => {
              if (data) {
                // Update list item, does not need to refresh list
                (item.data as any).statusAt = (data.header as any).statusAt;
                item.status = data.header.status;
                if (updatePeriodicInvoices) {
                  this.refreshPeriodicInvoices().then(() => {
                    arr.push(data);
                  });
                } else {
                  arr.push(data);
                }
              }
              (item as any)["isReading" + (status || "")] = false;
              return;
            });
          } else {
            return;
          }
        });
      },
      getSepaData: (
        /** Array for invoices */
        _arr: Invoice[]
      ): Promise<string> => undefined,
      getFinvoiceData: (
        /** Array for invoices */
        _arr: Invoice[]
      ): Promise<Blob[]> => undefined,
      getPdfData: (
        /** Array for invoices */
        _arr: Invoice[]
      ): Promise<Blob[]> => undefined,
      /** Optional label for data export */
      exportLabel: null,
      /** Indicates if the export is not possible */
      disabled: () => false,
    };
  };

  /**
   * Returns PDF url for a single invoice.
   * @param item Invoice list item.
   */
  public getPdfUrl(item: InvoiceListItem): string {
    if (!(item as any).pdfUrl) {
      (item as any).pdfUrl = this.getContentUrl(item.id, false);
    }
    return (item as any).pdfUrl;
  }

  /**
   * Retrieves the QR code for the specified invoice item.
   * If the QR code is not already cached, it generates the QR code and caches it for future use.
   * @param item - The invoice item for which to retrieve the QR code.
   * @returns The QR code string.
   */
  public getQrCode(item: InvoiceListItem): string {
    if (!this.qrCodesCache[item.id]) {
      this.qrCodesCache[item.id] = "loading";
      this.generateQrCodeSvgForInvoice(item).then((qrCode) => {
        if (!qrCode) {
          this.qrCodesCache[item.id] = "error";
          this.$timeout();
          return this.qrCodesCache[item.id];
        } else {
          this.qrCodesCache[item.id] = qrCode;
          this.$timeout();
          return this.$sce.trustAsHtml(this.qrCodesCache[item.id]);
        }
      });
    }
    if (this.qrCodesCache[item.id] === "error") {
      return this.qrCodesCache[item.id];
    }
    return this.$sce.trustAsHtml(this.qrCodesCache[item.id]);
  }

  /**
   * Calculates barcode value for the invoice item.
   * @param item Invoice list item.
   */
  public getBarcodeValue(item: InvoiceListItem): string {
    return Barcodes.getValue(
      item.otherPartyInfo.ibanNumber || "",
      item.entityType === "net" ? "" : item.reference,
      Dates.asDate(item.data.dueDate),
      Numeric.round(this.getInvoicePayment(item) * 100, 0)
    );
  }

  /**
   * Calculates the invoice payment amount for a given invoice item.
   * If the invoice item is of type "tax" or "union" and the total paid amount
   * is different from the payment amount, it returns the rounded difference
   * between the payment amount and the total paid amount.
   * Otherwise, it returns the payment amount.
   * @param {InvoiceListItem} item - The invoice item to calculate the payment for.
   * @returns {number} - The calculated payment amount.
   */
  public getInvoicePayment(item: InvoiceListItem): number {
    if (
      (item.entityType == "tax" || item.entityType == "union") &&
      (item.data as any)?.totalPaid &&
      (item.data as any).totalPaid != item.payment
    ) {
      return Numeric.round(item.payment - (item.data as any).totalPaid);
    }
    return item.payment;
  }

  /**
   * Retrieves the barcode for the invoice recipient's IBAN.
   * If the IBAN does not start with "fi" or is not provided, an empty string is returned.
   * The barcode is generated using the Barcodes.createBarcode function.
   * @returns The barcode as a string, wrapped in an HTML trust object.
   */
  public getBarcode(item: InvoiceListItem): string {
    if (
      !item.otherPartyInfo.ibanNumber ||
      !item.otherPartyInfo.ibanNumber.toLowerCase().trim().startsWith("fi")
    ) {
      return "";
    }
    const getDocumentAndSvgNode = () => {
      return {
        document,
        svgNode: document.createElementNS("http://www.w3.org/2000/svg", "svg"),
      };
    };

    const result = Barcodes.createBarcode(
      getDocumentAndSvgNode,
      item.otherPartyInfo.ibanNumber || "",
      item.entityType === "net" ? "" : item.reference || "",
      Dates.asDate(item.data.dueDate),
      Numeric.round(this.getInvoicePayment(item) * 100, 0),
      {
        width: 1.8,
        height: 32,
        displayValue: true,
        fontSize: 15,
      }
    );
    if (!result) {
      return "";
    }
    return this.$sce.trustAsHtml(
      new XMLSerializer().serializeToString(result as any)
    );
  }

  /**
   * Copies the barcode to clipboard.
   * @param item Invoice list item.
   */
  public copyBarcode(item: InvoiceListItem) {
    (navigator as any).clipboard.writeText(this.getBarcodeValue(item));
  }

  /**
   * Displays the PDF for the given invoice items.
   * @param items - The list of invoice items.
   */
  public showloadPfd(items: InvoiceListItem[]) {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    const url = `${
      Configs.current.reportServer
    }/pdf?id=&reportType=${encodeURIComponent(
      "invoiceList"
    )}&template=${encodeURIComponent(
      "invoiceList"
    )}&language=${encodeURIComponent(
      Translations.getLanguage()
    )}&disposition=inline&access_token=${this.companyAjax.getCurrentToken()}`;
    this.handlePdfRequest(url, false, { object: items }, loading);
  }

  /**
   * Generates a URL for the calculation details of an invoice item.
   * @param item - The invoice item for which to generate the URL.
   * @returns A string representing the URL for the calculation details.
   */
  public calculationHref(item: InvoiceListItem): string {
    //TODO: Nice to have for periodic invoices implement list for viewing invoice rows
    if(item.status == InvoiceStatus.Canceled &&
      (item.entityType == InvoiceType.Net || item.entityType == InvoiceType.Foreclosure) &&
      item.businessObjects.length == 1
    ){
      return `#/calc/details/${item.businessObjects[0]}`;
    }
    return "";
  }

  private getEprWarning(
    items: InvoiceListItem[],
    status: InvoiceStatus
  ): string {
    if (status === InvoiceStatus.Canceled) {
      for (const item of items) {
        if (
          item.status === InvoiceStatus.Paid &&
          item.entityType == InvoiceType.Net &&
          this.isDelayed(item.logicalDate)
        ) {
          return Translations.get("SALAXY.NG1.InvoicesCompany.invoiceReader.eprWarningTextCanceled");
        }
      }
    } else if (status === InvoiceStatus.Paid) {
      for (const item of items) {
        if (
          item.status !== InvoiceStatus.Paid &&
          item.entityType == InvoiceType.Net &&
          this.isDelayed(item.logicalDate)
        ) {
          return Translations.get("SALAXY.NG1.InvoicesCompany.invoiceReader.eprWarningTextPaid");
        }
      }
    }
    return null;
  }
  /** Checks if the Epr report is delayed. */
  private isDelayed(salaryDate?: string): boolean {
    if (this.session.isInRole(SystemRole.Test)) {
      const officialDate = Dates.add(salaryDate ?? "today", 5, "day");
      const expiresAt = Dates.addWorkdays(officialDate, 0);
      const today = Dates.asDate("today");
      return today > expiresAt;
    }
    return false;
  }
  private getInvoice(
    item: InvoiceListItem,
    message: any,
    status: InvoiceStatus = null
  ): Promise<Invoice> {
    const updateInvoiceStatus = (
      invoice: Invoice,
      status: InvoiceStatus
    ): Promise<Invoice> => {
      if (invoice.header.status === status) {
        return Promise.resolve(invoice);
      }
      return this.invoicesApi.requestStatusUpdate(item.id, status);
    };

    return this.api.getSingle(item.id).then((readInvoice: Invoice) => {
      if (status) {
        return updateInvoiceStatus(readInvoice, status).then(
          (updatedInvoice) => {
            item.status = updatedInvoice.header.status;
            return updatedInvoice;
          }
        );
      } else {
        return readInvoice;
      }
    });
  }

  private async generateQrCodeSvgForInvoice(
    item: InvoiceListItem
  ): Promise<string> {
    const svg = await QrCodes.getValue(
      BicHelper.getBicForIban(
        item.otherPartyInfo.ibanNumber.replace(/\s+/g, "")
      ),
      item.otherPartyInfo.avatar.displayName,
      item.otherPartyInfo?.ibanNumber,
      item.entityType === "net" ? null : item.reference,
      item.entityType === "net"
        ? `${Translations.get(
            `SALAXY.ENUM.InvoiceType.${item.entityType}.label`
          )} ${Dates.getFormattedRange(
            item.data.periodStart,
            item.data.periodEnd
          )}`
        : null,
      this.getInvoicePayment(item),
      item.endAt
    );
    return svg;
  }

  private async handlePdfRequest(
    url: string,
    inline: boolean,
    payload: any,
    loading: any
  ): Promise<void> {
    try {
      await this.fetchPdf(url, payload, inline);
    } catch (error) {
      //HACK: Implement failover functionality to cloud (azure)
      if (
        url.includes(Configs.current.reportServer) &&
        Configs.current.getEnv() === "prod"
      ) {
        url = url.replace(
          Configs.current.reportServer,
          "https://failover-reports.salaxy.com"
        );
        try {
          await this.fetchPdf(url, payload, inline);
        } catch (error) {
          console.error(error);
        }
      } else {
        console.error(error);
      }
    }
    if (loading) {
      loading.dismiss();
    }
  }

  private async fetchPdf(
    url: string,
    payload: any,
    inline: boolean
  ): Promise<void> {
    let response: any;
    if (payload) {
      response = await this.$http.post(url, payload, { responseType: "blob" });
    } else {
      response = await this.$http.get(url, { responseType: "blob" });
    }
    if (response.data) {
      if (inline) {
        this.uiHelpers.openFile(response.data as any);
      } else {
        this.uiHelpers.downloadFile(
          response.data as any,
          decodeURIComponent(response.headers("content-disposition")).match(
            /filename\s*=\s*"?(?<file>.*?)(?:\s*;|"|\s*$)/
          )?.groups?.file
        );
      }
    }
  }

  private async refreshPeriodicInvoices(): Promise<any[]> {
    const periodicInvoices = this.odataController.items.filter(
      (x) =>
        x.entityType === InvoiceType.Tax ||
        x.entityType === InvoiceType.Union ||
        x.entityType === InvoiceType.Payroll ||
        x.entityType === InvoiceType.Foreclosure
    );
    if (periodicInvoices.length === 0) {
      return Promise.resolve([]);
    }
    const $filter = periodicInvoices.map((x) => `id eq '${x.id}'`).join(" or ");
    const loading = this.uiHelpers.showLoading(Translations.get("SALAXY.NG1.InvoicesCompany.gettingsInvoices"));
    const delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));
    await delay(3000);
    return this.odataController
      .getApi()
      .getOData({ $filter })
      .then((result) => {
        const toBeRemoved = [];
        for (const periodicInvoice of periodicInvoices) {
          const item = result.items.find((x) => x.id === periodicInvoice.id);
          if (item) {
            angular.copy(item, periodicInvoice);
          } else {
            toBeRemoved.push(periodicInvoice);
          }
        }
        toBeRemoved.forEach((x) => {
          const index = this.odataController.items.indexOf(x);
          if (index > -1) {
            this.odataController.items.splice(index, 1);
          }
        });
        loading.dismiss();
        return periodicInvoices;
      })
      .catch((error) => {
        loading.dismiss();
        console.error(error);
        return periodicInvoices;
      });
  }
}
